// Idle prefetch — after the first screen paints, quietly warm the API cache for
// the other sections so the user's first click into each tab is instant (served
// from the stale-while-revalidate cache in api.ts).
//
// SAFETY:
//  • Every call below is read-only. The one endpoint with a write side effect
//    (GET /new-sales also syncs a store) is deliberately NOT data-prefetched —
//    only its filters are warmed.
//  • Each call mirrors EXACTLY what the matching page fires on mount (same
//    function + same arguments → identical request → same cache key), so it
//    genuinely hits the page's first load rather than warming a stray entry.
//  • All failures are swallowed; a miss just means that tab loads at normal
//    speed. Nothing here can block the UI or change any data.
import {
  fetchFilters,
  fetchPayments,
  fetchInBankSummary,
  fetchRefunds,
  fetchRefundFilters,
  fetchDisputes,
  fetchDisputeFilters,
  fetchInventory,
  fetchInventoryFilters,
  fetchNewSalesFilters,
  fetchDelayedPayments,
  fetchEomTable,
  fetchEOMSummary,
  fetchEOMadjustments,
  fetchRetentionPipelineMonths,
  fetchTeamPerformanceMonths,
  fetchDialingReport,
  fetchStatsMonths,
  fetchStats,
  fetchMySales,
  // [TAB-INSTANT 2026-08-20] The tabs that were never warmed + the re-warm trigger.
  fetchProcessAnalysis,
  fetchPmRecommendations, // [PM-RECS PERF 2026-08-25]
  fetchSalesScoreboard,
  fetchPerformanceTeams,
  fetchTeamPerformance,
  fetchPmLeaderboard,
  fetchRetentionPipeline,
  fetchNewSales,
  fetchMyPerformance, // [TAB-SPEED 2026-09-10]
  fetchActivityLogs,  // [TAB-SPEED 2026-09-10]
  onDataChange,
} from './api';
import { effectiveCurrentMonth } from '../utils/effectiveMonth';

// Mirror the pages' own month helpers so prefetch keys line up. [EFFECTIVE-MONTH
// 2026-07-01] Uses the shared effective-current-month util (12:30 PM IST on the 1st
// rollover) — the SAME function the pages' getCurrentMonth delegates to — so the warmed
// cache key matches the page's first fetch exactly (including during the grace window).
function currentMonth(): string {
  return effectiveCurrentMonth();
}

let started = false;
// [EDIT-COLUMNS 2026-09-28] The signed-in admin's saved Payments extra columns. The Payments warm must
// send the SAME extraCols the page sends, or its cache key never matches. Set by AppLayout at sign-in
// (from storage) and by PaymentsPage on every Apply; [] for everyone else.
let warmPaymentExtraCols: string[] = [];
export function setWarmPaymentExtraCols(cols: string[]): void { warmPaymentExtraCols = cols; }
// [TAB-INSTANT 2026-08-20] Trailing debounce for the post-write re-warm below.
let rewarmTimer: ReturnType<typeof setTimeout> | null = null;
const REWARM_DEBOUNCE_MS = 1600;

// Run `fn` when the browser is idle (bounded), so warming never competes with a paint.
function onIdle(fn: () => void, timeout = 3000): void {
  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fn, { timeout });
  else setTimeout(fn, 1200);
}

// [TAB-INSTANT 2026-08-20] The full warm sweep — extracted from warmCaches() so the
// post-write re-warm can reuse it verbatim. Every task mirrors its page's mount call
// exactly (same function + same arguments → same cache key); failures are swallowed.
const warmAll = () => {
    const cur = currentMonth();

    const tasks: Array<Promise<unknown>> = [
      // EOM dashboard (common landing-adjacent tab)
      fetchEOMSummary(cur),
      fetchEOMadjustments(cur),
      // Payments
      fetchFilters(),
      fetchInBankSummary({ month: cur }),
      // [KEY-MATCH 2026-08-13] `months: [cur]`, NOT `month: cur`. Payments/Refunds/Disputes all
      // went multi-select on 2026-07-15 and their pages now send `months`, which monthsParams
      // flattens to a `months=` CSV — a DIFFERENT cache key from `month=`. These three warms had
      // been landing on keys no page ever asks for, so every one of them was wasted work.
      fetchPayments({ page: 1, pageSize: 0, sortBy: 'payment_date', sortOrder: 'desc', months: [cur],
        extraCols: warmPaymentExtraCols.length ? warmPaymentExtraCols : undefined }),
      // Refunds — Refunds/Disputes sort CLIENT-side, so their mount fetch sends NO sort
      // params; mirror that here (with the canonicalized cache key, the empty account/search
      // are ignored) so the warm actually hits the page's first load.
      fetchRefundFilters(),
      fetchRefunds({ page: 1, pageSize: 0, months: [cur] }),
      // Disputes
      fetchDisputeFilters(),
      fetchDisputes({ page: 1, pageSize: 0, months: [cur] }),
      // Inventory — CURRENT month. [INV-MONTH 2026-06-29] the tab's landing month was changed
      // from next month to the current one (InventoryPage `initialMonth`), but this warm was
      // never updated, so for 6 weeks it fetched a month the tab does not open on. Sort is
      // client-side, and the page's empty type/pm/brand/status arrays are dropped by cacheKey,
      // so month is the only param that has to line up.
      fetchInventoryFilters(cur),
      fetchInventory({ page: 1, pageSize: 0, month: cur }),
      // New Sales — filters only (its data GET also syncs a store).
      fetchNewSalesFilters(),
      // My Sales — read-only (no write side effect). Mirrors MySalesPage's mount call
      // exactly (default month, no person → the server scopes it) so the first open is
      // instant, and it also warms the shared server-side HubSpot deal cache for everyone.
      // [MULTI-MONTH 2026-07-15] fetchMySales takes a months LIST now — pass the
      // default month as a single-item array so this still mirrors MySalesPage's
      // mount call exactly and the prefetch cache key keeps lining up.
      fetchMySales([cur]),
      // Cheap, side-effect-free endpoints for the remaining tabs.
      fetchDelayedPayments(),
      fetchEomTable(),
      fetchRetentionPipelineMonths(),
      fetchTeamPerformanceMonths(),
      // Dialing Report — read-only; no args mirrors its on-mount call exactly so
      // the first click is served instantly from cache.
      fetchDialingReport(),
      // Stats — warm the month list, then the latest month's table (mirrors StatsPage's
      // mount: months → default to latest → fetch that month). Was previously missing.
      fetchStatsMonths().then((m) => (m && m[0] ? fetchStats(m[0]) : undefined)),
      // ── [TAB-INSTANT 2026-08-20] Tabs that were never warmed ─────────────────
      // Process Analysis — one GET returns the whole tab (permanently warm server-side).
      fetchProcessAnalysis(),
      // [PM-RECS PERF 2026-08-25] PM Recommendations — one GET, default month, permanently warm
      // server-side; from here the tab opens from cache with only the deliberate skeleton dwell.
      fetchPmRecommendations(),
      // Sales Scoreboard — both pill periods; each period is its own cache entry.
      fetchSalesScoreboard('last6'),
      fetchSalesScoreboard('current'),
      // PM Performance — the team tabs plus the Overall tab's data (leaderboard +
      // table), mirroring PerformancePage/TeamPerformancePage mounts exactly.
      fetchPerformanceTeams(),
      fetchTeamPerformance('overall', cur),
      fetchPmLeaderboard('overall'),
      // Retention Analysis — the page defaults to (and stays on) the current month.
      fetchRetentionPipeline(cur),
      // New Sales — the DATA too, now safe: `prefetch: true` tells the server to skip
      // this GET's store-sync side effect, making the warm genuinely read-only. The
      // month mirrors the page's default; a restored non-default selection just misses.
      fetchNewSales({ months: [cur] }, { prefetch: true }),
      // [TAB-SPEED 2026-09-10] Performance Review + Application Logs were never warmed. Both mirror
      // their page's mount call exactly (no PM → the server picks the viewer's own; logs page 1 with
      // every filter at its default, empty strings dropped by cacheKey). A user without the section
      // gets a quick 403, swallowed like every other miss here.
      fetchMyPerformance(),
      fetchActivityLogs({ page: 1, pageSize: 50, section: 'all', search: '', success: 'all', userEmail: '', from: '', to: '' }),
    ];

    tasks.forEach((t) => t.catch(() => {}));
};

/** Warm caches once per session, on idle, without ever blocking or throwing. */
export function warmCaches(): void {
  if (started) return;
  started = true;
  onIdle(warmAll);

  // [TAB-INSTANT 2026-08-20] Re-warm after every successful write. A write clears the
  // WHOLE client GET cache (api.ts) — so before this, any save sent every other tab back
  // to a cold skeleton and nothing ever re-warmed them mid-session. Debounced so a burst
  // of saves triggers ONE sweep, and idle-scheduled so it never competes with the active
  // page's own immediate refetch (which runs ~120ms after the write and re-fills the
  // cache these tasks then mostly hit without extra network).
  onDataChange(() => {
    if (rewarmTimer) clearTimeout(rewarmTimer);
    rewarmTimer = setTimeout(() => {
      rewarmTimer = null;
      onIdle(warmAll);
    }, REWARM_DEBOUNCE_MS);
  });
}
